import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import AppContext from '../../contexts/AppContext';
import AppDivisionContext from '../../contexts/AppDivisionContext';
import { ProjectContext } from '../../contexts/ProjectContext';
import { ProjectListProvider } from '../../contexts/ProjectListContext';
import RolesContext from '../../contexts/RolesContext';
import { useOffline } from '../../contexts/OfflineContext';
import { usePageActions } from '../../contexts/PageActionsContext';
import { getDivisionResourceName } from '../../hooks/divisionResource';
import divisionApi from '../../utils/api/division';
import projectApi from '../../utils/api/project';
import DivisionError from '../Shared/DivisionError/DivisionError';
import ResourceIndex from '../Shared/ResourceIndex/ResourceIndex';
import ProjectForm from './Forms/ProjectForm';
import ProjectList from './ProjectList';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { Modal } from '@sw-sw/lib-ui';
import rolesApi from '../../utils/api/roles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';

const STATUS_OPTIONS = [
  {
    key: 'pending',
    label: 'Pending Start',
    description: 'Project is awaiting approval or not yet underway.',
    dotColor: '#FF9F43',
    bg: 'rgba(255,159,67,0.08)',
    border: 'rgba(255,159,67,0.4)',
    color: '#b86a00',
  },
  {
    key: 'active',
    label: 'Active Now',
    description: 'Work is ready to begin immediately.',
    dotColor: '#48af98',
    bg: 'rgba(72,175,152,0.08)',
    border: 'rgba(72,175,152,0.4)',
    color: '#2a7a69',
  },
];

function ProjectAdd({ handleClose }) {
  const [redirectId, setRedirectId] = useState(null);
  const [customTab, setCustomTab] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const appStore = useContext(AppContext);
  const projectContext = useContext(ProjectContext);
  const { getPath } = useContext(AppDivisionContext);

  const handleFormSubmit = useCallback((data) => {
    setPendingFormData(data);
    setShowStatusModal(true);
    return new Promise(() => {});
  }, []);

  const createWithStatus = async (status) => {
    setCreating(true);
    try {
      const project = await projectApi.create({ ...pendingFormData, status });
      appStore.triggerSuccessNotification();
      setCustomTab(project.client.custom_tab);
      setRedirectId(project.id);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    projectContext.clear();
  }, []);

  if (redirectId) {
    return customTab
      ? <Redirect to={getPath(`/projects/${redirectId}/project-docs`)} push />
      : <Redirect to={getPath(`/projects/${redirectId}/inspections`)} push />;
  }

  if (showStatusModal) {
    return (
      <Modal
        title='How would you like to start this project?'
        handleClose={() => { setShowStatusModal(false); setPendingFormData(null); }}
        show={true}
        dismissOnBgClick={false}
      >
        <div className='project-status-select'>
          <p className='project-status-select__subtitle'>You can change this at any time from the project page.</p>
          <div className='project-status-select__options'>
            {STATUS_OPTIONS.map(({ key, label, description, dotColor, bg, border, color }) => (
              <button
                key={key}
                type='button'
                disabled={creating}
                className='project-status-select__option'
                style={{ background: bg, borderColor: border, color }}
                onClick={() => createWithStatus(key)}
              >
                <span className='project-status-select__dot' style={{ background: dotColor }} />
                <span className='project-status-select__option-text'>
                  <strong>{label}</strong>
                  <span>{description}</span>
                </span>
              </button>
            ))}
          </div>
          <button
            type='button'
            className='project-status-select__back'
            onClick={() => setShowStatusModal(false)}
            disabled={creating}
          >
            ← Back to form
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <ProjectForm
      handleClose={handleClose}
      submitBtnText={'Create'}
      handleSubmit={handleFormSubmit}
      hideStartDate={false}
      initialData={pendingFormData || undefined}
    />
  );
}

function ProjectFilter({ handleClose }) {
  const projectContext = useContext(ProjectContext);
  const { appDivisionId } = useContext(AppDivisionContext);
  const [projectType, setProjectType] = useState([]);
  const [primaryContact, setPrimaryContact] = useState([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState([]);
  const [primaryContactOptions, setPrimaryContactOptions] = useState([]);
  const [error, setError] = useState(false);
  const animatedComponents = makeAnimated();

  const handleSubmit = () => {
    if (projectType.length === 0 && primaryContact.length === 0) {
      setError(true);

      return Promise.resolve();
    }

    projectContext.setFilterProjectType(projectType);
    projectContext.setFilterProjectPrimaryContact(primaryContact);

    handleClose();

    return Promise.resolve();
  };

  const handleClear = () => {
    setProjectType([]);
    setPrimaryContact([]);
    projectContext.setFilterProjectType([]);
    projectContext.setFilterProjectPrimaryContact([]);
    setError(false);
    handleClose();
  };

  useEffect(() => {
    projectApi.getProjectTypes().then((data) => {
      setProjectTypeOptions(
        data.map((type) => {
          return { value: type.id, label: type.name };
        }),
      );
    });
    rolesApi.primaryContactDropdown(appDivisionId).then((data) => {
      setPrimaryContactOptions(
        data.data.map((contact) => {
          return { value: contact.value, label: contact.label };
        }),
      );
    });
    setProjectType(projectContext.filterProjectType);
    setPrimaryContact(projectContext.filterProjectPrimaryContact);
  }, []);

  return (
    <Modal
      title='Filter Projects'
      handleClose={handleClose}
      dismissOnBgClick={true}
      handleSubmit={handleSubmit}
      customButtonText='Clear'
      handleCustom={handleClear}
      show={true}
    >
      <label>Project Type</label>
      <Select
        closeMenuOnSelect={false}
        isMulti
        components={animatedComponents}
        placeholder='Project Type'
        onChange={(selected) => setProjectType(selected)}
        options={projectTypeOptions}
        value={projectType}
        isClearable={true}
        className='project-select-dropdown'
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e8e4df',
          }),
          control: (provided) => ({
            ...provided,
            borderColor: '#cecac2',
            boxShadow: 'none',
            ':hover': {
              borderColor: '#48af98',
              boxShadow: '0 0 0 1px #48af98',
            },
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#f9f5ed' : 'white',
            color: '#002230',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#48af98',
              color: 'white',
            },
          }),
        }}
        menuPortalTarget={document.body}
      />
      <label>Primary Contact</label>
      <Select
        closeMenuOnSelect={false}
        isMulti
        components={animatedComponents}
        className='project-select-dropdown'
        placeholder='Primary Contact'
        noOptionsMessage={() =>
          'No User Assigned as Primary Contact in Projects'
        }
        onChange={(selected) => setPrimaryContact(selected)}
        options={primaryContactOptions}
        value={primaryContact}
        isClearable={true}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e8e4df',
          }),
          control: (provided) => ({
            ...provided,
            borderColor: '#cecac2',
            boxShadow: 'none',
            ':hover': {
              borderColor: '#48af98',
              boxShadow: '0 0 0 1px #48af98',
            },
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#f9f5ed' : 'white',
            color: '#002230',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#48af98',
              color: 'white',
            },
          }),
        }}
        menuPortalTarget={document.body}
      />

      {error && (
        <p className='precipitation-error-msg-wrapper'>
          Please select at least one Project Type or Primary Contact
        </p>
      )}
    </Modal>
  );
}

const STATUS_TABS = [
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'archived', label: 'Archived' },
];

const STATUS_BADGE_STYLE = {
  active: { background: '#48af98', color: '#fff' },
  pending: { background: '#f59e0b', color: '#fff' },
  archived: { background: '#9ca3af', color: '#fff' },
};

function Projects() {
  const [statusTab, setStatusTab] = useState('active');
  const [statusCounts, setStatusCounts] = useState({ active: 0, pending: 0, archived: 0 });
  const roleStore = useContext(RolesContext);
  const appDivisionContext = useContext(AppDivisionContext);
  const projectContext = useContext(ProjectContext);
  const [showBulkStorm, setShowBulkStorm] = useState(false);
  const isOffline = useOffline();
  const [resourceSetPage, setResourceSetPage] = useState(null);
  const { setPageActions } = usePageActions();
  const openCreateRef = useRef(null);
  const openFilterRef = useRef(null);

  const canCreate = !isOffline && statusTab !== 'archived' && roleStore.userHasPermission('create', 'Project');

  const hasActiveFilters =
    projectContext.filterProjectType.length > 0 ||
    projectContext.filterProjectPrimaryContact.length > 0;

  useEffect(() => {
    if (appDivisionContext.appDivisionId) {
      divisionApi.projects.statusCounts(appDivisionContext.appDivisionId).then(setStatusCounts);
    }
  }, [appDivisionContext.appDivisionId]);

  useEffect(() => {
    setPageActions(
      <div className='top-nav-page-actions'>
        {canCreate && (
          <button className='primary' onClick={() => openCreateRef.current?.()}>
            Create Project
          </button>
        )}
        {!isOffline && (
          <button
            className={`outline overflow${hasActiveFilters ? ' filter-active' : ''}`}
            onClick={() => openFilterRef.current?.()}
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>
        )}
      </div>
    );
    return () => setPageActions(null);
  }, [canCreate, isOffline, hasActiveFilters]);

  const handleTabChange = (tab) => {
    setStatusTab(tab);
    if (typeof resourceSetPage === 'function') {
      resourceSetPage(1);
    }
  };

  const dismissTypeFilter = (valueToRemove) => {
    const updated = projectContext.filterProjectType.filter((f) => f.value !== valueToRemove);
    projectContext.setFilterProjectType(updated);
  };

  const dismissContactFilter = (valueToRemove) => {
    const updated = projectContext.filterProjectPrimaryContact.filter((f) => f.value !== valueToRemove);
    projectContext.setFilterProjectPrimaryContact(updated);
  };

  const renderList = useCallback(
    (_, projects, onClick) => (
      <ProjectList
        isArchived={statusTab === 'archived'}
        projects={projects}
        handleClick={(id, index) => onClick(index)}
        showBulkStorm={showBulkStorm}
        handleCancel={() => setShowBulkStorm(false)}
      />
    ),
    [showBulkStorm, statusTab],
  );

  if (!appDivisionContext.appDivisionId) {
    return <DivisionError resource='projects' />;
  }

  return (
    <ProjectListProvider>
      <ResourceIndex
        onPageControls={({ setPage }) => setResourceSetPage(() => setPage)}
        onRegisterActions={(openCreate, openFilter) => {
          openCreateRef.current = openCreate;
          openFilterRef.current = openFilter;
        }}
        pageNameForStyling={'project'}
        resourceName={getDivisionResourceName('projects')}
        resourceQueryProps={{
          divisionId: appDivisionContext.appDivisionId,
          projectTypes: projectContext.filterProjectType,
          primaryContacts: projectContext.filterProjectPrimaryContact,
          status: statusTab,
        }}
        resourceTableHeaders={[['ID', 'id']]}
        enableRead={roleStore.userHasPermission('read', 'Project')}
        enableEdit={false}
        enableCallToAction={
          !isOffline &&
          statusTab !== 'archived' &&
          roleStore.userHasPermission('create', 'Project')
        }
        enableSearch={!isOffline && true}
        callToActionContent='Create Project'
        enablePagination
        enableCallToFilter={!isOffline}
        searchInputPlaceholder='Search Projects'
        renderReader={(project) => {
          if (statusTab === 'archived') {
            return (
              <Redirect
                to={{
                  pathname: appDivisionContext.getPath(
                    `/projects/archive/${project.id}`,
                  ),
                  state: { project },
                }}
                push
              />
            );
          } else {
            return project.client.custom_tab ? (
              <Redirect
                to={{
                  pathname: appDivisionContext.getPath(
                    `/projects/${project.id}/project-docs`,
                  ),
                  state: { project },
                }}
                push
              />
            ) : (
              <Redirect
                to={{
                  pathname: appDivisionContext.getPath(
                    `/projects/${project.id}/inspections`,
                  ),
                  state: { project },
                }}
                push
              />
            );
          }
        }}
        renderCallToAction={(handleClose) => {
          return <ProjectAdd handleClose={handleClose} />;
        }}
        renderCallToFilter={(handleClose) => {
          return <ProjectFilter handleClose={handleClose} />;
        }}
        renderList={renderList}
        filterActive={hasActiveFilters}
        headerSlot={
          <div className='project-status-tabs'>
            {STATUS_TABS.map(({ key, label }) => (
              <button
                key={key}
                className={`project-status-tab${statusTab === key ? ' active' : ''}`}
                onClick={() => handleTabChange(key)}
              >
                <span className='project-status-tab__label'>{label}</span>
                <span
                  className='project-status-badge'
                  style={STATUS_BADGE_STYLE[key]}
                >
                  {statusCounts[key] ?? 0}
                </span>
              </button>
            ))}
          </div>
        }
      />
      {hasActiveFilters && (
        <div className='project-filter-chips'>
          {projectContext.filterProjectType.map((f) => (
            <span key={f.value} className='filter-chip'>
              {f.label}
              <button
                className='filter-chip-dismiss'
                onClick={() => dismissTypeFilter(f.value)}
                aria-label={`Remove ${f.label} filter`}
              >
                ×
              </button>
            </span>
          ))}
          {projectContext.filterProjectPrimaryContact.map((f) => (
            <span key={f.value} className='filter-chip'>
              {f.label}
              <button
                className='filter-chip-dismiss'
                onClick={() => dismissContactFilter(f.value)}
                aria-label={`Remove ${f.label} filter`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </ProjectListProvider>
  );
}

export default Projects;
